/** The sortable grid. Wraps the cards in dnd-kit's `DndContext` + `SortableContext`
 *  and, on drag end, computes the new id order and hands it up. Reordering itself
 *  (optimistic cache write + `api.reorder` + rollback) is the parent's job — this
 *  component only reports the intended order. Pointer + keyboard sensors both work.
 *  In view mode the same cards render without drag behaviour. */
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Box } from '@mui/material';

import { ImageCard } from './ImageCard';
import type { GalleryImage } from './types';

/** Pure reorder: given the current id order and a dnd-kit active/over pair, return
 *  the new id order (or the original if the move is a no-op / ids are unknown).
 *  Exported so the reorder contract can be tested without jsdom drag geometry. */
export function reorderIds(ids: number[], activeId: number, overId: number): number[] {
  if (activeId === overId) return ids;
  const oldIndex = ids.indexOf(activeId);
  const newIndex = ids.indexOf(overId);
  if (oldIndex < 0 || newIndex < 0) return ids;
  return arrayMove(ids, oldIndex, newIndex);
}

/** The narrowest card: two columns from a 312 px row (a 360 px phone). */
export const GALLERY_CARD_MIN_PX = 150;

/** As many square cards a row as fit at {@link GALLERY_CARD_MIN_PX}, sharing the row's width
 *  — the grid fills it at any width (at 390 px fixed 175 px cards left one column and half
 *  the row empty). Near the 175 px THUMB on a desktop row. */
const GALLERY_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(${GALLERY_CARD_MIN_PX}px, 1fr))`,
  gap: 1.5,
} as const;

interface Props {
  images: GalleryImage[];
  canManage: boolean;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onReorder: (orderedIds: number[]) => void;
  onEdit: (image: GalleryImage) => void;
  onDelete: (image: GalleryImage) => void;
  onOpen: (image: GalleryImage) => void;
}

export function ImageGalleryGrid({
  images,
  canManage,
  selectedIds,
  onToggleSelect,
  onReorder,
  onEdit,
  onDelete,
  onOpen,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const ids = images.map((i) => i.id);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const next = reorderIds(ids, Number(active.id), Number(over.id));
    if (next !== ids) onReorder(next);
  };

  const cards = images.map((image, idx) => (
    <ImageCard
      key={image.id}
      image={image}
      isCover={idx === 0}
      canManage={canManage}
      selected={selectedIds.has(image.id)}
      onToggleSelect={onToggleSelect}
      onEdit={onEdit}
      onDelete={onDelete}
      onOpen={onOpen}
    />
  ));

  const layout = (
    <Box data-gallery-grid sx={GALLERY_GRID_SX}>
      {cards}
    </Box>
  );

  if (!canManage) return layout;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        {layout}
      </SortableContext>
    </DndContext>
  );
}
